import { getUserInfo } from './auth';
import { CommonUtils } from './commonUtils';
import { getApiV1Url, getCdnBaseUrl } from '../config/api';
import { parseApiDateTime } from './dateTime';

export type StorageDomain =
  | 'PROJECT'
  | 'BRAND'
  | 'SHOP'
  | 'PRODUCT'
  | 'VOUCHER'
  | 'BOARD_POST'
  | 'MEMBERSHIP'
  | 'APP';

export interface PresignUploadFileInput {
  fileName: string;
  contentType: string;
  fileSize: number;
}

export interface PresignUploadItem {
  objectKey: string;
  originalFileName: string;
  storedFileName: string;
  contentType: string;
  fileSize: number;
  presignedUrl: string;
  expiresAt: string;
}

export interface PresignUploadResponse {
  timestamp: number;
  errors?: {
    error: Array<{
      code: number;
      target: string;
      message: string;
    }>;
  };
  data: {
    uploads: PresignUploadItem[];
  };
}

export interface S3UploadResult {
  ok: boolean;
  status: number;
  statusText: string;
  headers: Record<string, string>;
  body: string | null;
}

/** presign + S3 업로드 완료 후 도메인 API에 전달하는 파일 메타 */
export interface UploadFilePayload {
  tempKey: string;
  tempFileName: string;
  originalName: string;
  contentType: string;
  declaredFileSize: number;
}

export interface PresignUploadParams {
  domain: StorageDomain;
  files: PresignUploadFileInput[];
  projectCode?: string;
}

export interface PresignDownloadItem {
  s3Uri: string;
  objectKey: string;
  presignedUrl: string;
  expiresAt: string;
}

export interface PresignDownloadResponse {
  timestamp: number;
  errors?: {
    error: Array<{
      code: number;
      target: string;
      message: string;
    }>;
  };
  data: {
    downloads: PresignDownloadItem[];
  };
}

const EXTENSION_CONTENT_TYPE: Record<string, string> = {
  png: 'image/png',
  jpg: 'image/jpeg',
  jpeg: 'image/jpeg',
  webp: 'image/webp',
};

export const ALLOWED_IMAGE_CONTENT_TYPES = [
  'image/png',
  'image/jpeg',
  'image/jpg',
  'image/webp',
] as const;

/** file.type이 비어 있을 때 확장자로 content-type 보정 */
export const resolveImageContentType = (file: File): string | null => {
  const normalizedType = file.type.trim().toLowerCase();
  if (normalizedType === 'image/jpg') {
    return 'image/jpeg';
  }
  if (ALLOWED_IMAGE_CONTENT_TYPES.includes(normalizedType as (typeof ALLOWED_IMAGE_CONTENT_TYPES)[number])) {
    return normalizedType === 'image/jpg' ? 'image/jpeg' : normalizedType;
  }

  const ext = file.name.split('.').pop()?.toLowerCase();
  if (ext && ext in EXTENSION_CONTENT_TYPE) {
    return EXTENSION_CONTENT_TYPE[ext];
  }

  return null;
};

const LOG_PREFIX = '[Storage]';

const logStorage = (step: string, detail?: unknown) => {
  if (detail !== undefined) {
    console.log(`${LOG_PREFIX} ${step}`, detail);
  } else {
    console.log(`${LOG_PREFIX} ${step}`);
  }
};

const logStorageError = (step: string, detail?: unknown) => {
  if (detail !== undefined) {
    console.error(`${LOG_PREFIX} ${step}`, detail);
  } else {
    console.error(`${LOG_PREFIX} ${step}`);
  }
};

const truncatePresignedUrl = (url: string): string => {
  try {
    const parsed = new URL(url);
    return `${parsed.origin}${parsed.pathname}?…(query ${parsed.search.length} chars)`;
  } catch {
    return url.length > 120 ? `${url.slice(0, 120)}…` : url;
  }
};

const sanitizePresignDownloadResponseForLog = (
  result: PresignDownloadResponse
): PresignDownloadResponse => ({
  ...result,
  data: {
    downloads: (result.data?.downloads ?? []).map((item) => ({
      ...item,
      presignedUrl: truncatePresignedUrl(item.presignedUrl),
    })),
  },
});

const sanitizePresignResponseForLog = (result: PresignUploadResponse): PresignUploadResponse => ({
  ...result,
  data: {
    uploads: (result.data?.uploads ?? []).map((item) => ({
      ...item,
      presignedUrl: truncatePresignedUrl(item.presignedUrl),
    })),
  },
});

const getStorageApiHeaders = (): Record<string, string> => {
  const userInfo = getUserInfo();
  if (!userInfo) {
    throw new Error('사용자 정보가 없습니다. 로그인을 다시 시도해주세요.');
  }

  return {
    'Content-Type': 'application/json',
    'X-Account-Code': userInfo.accountCode,
    'X-User-Id': userInfo.userType === 'root' ? userInfo.accountCode : userInfo.userId,
  };
};

export const toUploadFilePayload = (upload: PresignUploadItem): UploadFilePayload => ({
  tempKey: upload.objectKey,
  tempFileName: upload.storedFileName,
  originalName: upload.originalFileName,
  contentType: upload.contentType,
  declaredFileSize: upload.fileSize,
});

export const toUploadFilesPayload = (uploads: PresignUploadItem[]): UploadFilePayload[] =>
  uploads.map(toUploadFilePayload);

const isPresentCacheVersion = (value?: string | number | null): value is string | number => {
  if (value === undefined || value === null) return false;
  return String(value).trim() !== '';
};

const toUnixSeconds = (value?: string | number | null): number | undefined => {
  if (value === undefined || value === null) return undefined;

  if (typeof value === 'number' && Number.isFinite(value)) {
    return Math.floor(value > 1e12 ? value / 1000 : value);
  }

  const raw = String(value).trim();
  if (!raw) return undefined;

  const normalized = raw.includes('T') ? raw : raw.replace(' ', 'T');
  const parsed = parseApiDateTime(normalized);
  if (!parsed) return undefined;

  return Math.floor(parsed.getTime() / 1000);
};

/**
 * CDN 캐시 버스팅 값.
 * `primaryVersion`이 있으면 그걸 쓰고, 없으면 `updatedAt`을 Unix 초로 변환합니다.
 */
export const toCdnCacheVersion = (
  version?: string | number | null,
  updatedAt?: string | number | null
): string | number | undefined => {
  if (isPresentCacheVersion(version)) {
    if (typeof version === 'number' && Number.isFinite(version)) {
      return version;
    }
    const raw = String(version).trim();
    if (/^-?\d+(\.\d+)?$/.test(raw)) {
      return Number(raw);
    }
    return toUnixSeconds(raw) ?? raw;
  }
  return toUnixSeconds(updatedAt);
};

/** 캐시 버스팅 쿼리 (`?v=` / `&v=`) */
export const appendCacheVersionQuery = (
  url: string,
  cacheVersion?: string | number | null
): string => {
  if (!isPresentCacheVersion(cacheVersion)) {
    return url;
  }

  const version = String(cacheVersion).trim();
  const separator = url.includes('?') ? '&' : '?';
  return `${url}${separator}v=${encodeURIComponent(version)}`;
};

export interface ResolveStoredFileUrlOptions {
  /** CDN 사용 시에만 `?v=`로 붙음. presign URL에는 붙이지 않음(서명 깨짐 방지) */
  cacheVersion?: string | number | null;
}

/**
 * 스토리지 URI → 표시용 URL
 * - VITE_CDN_BASE_URL 있음: CDN + optional ?v=
 * - 없음: presign-download (cacheVersion 미적용 — 서명 깨짐 방지)
 */
export const resolveStoredFileUrl = async (
  uri: string,
  options: ResolveStoredFileUrlOptions = {}
): Promise<string | null> => {
  const normalizedUri = uri.startsWith('/') ? uri : `/${uri}`;
  const cdnBase = getCdnBaseUrl();

  if (cdnBase) {
    return appendCacheVersionQuery(`${cdnBase}${normalizedUri}`, options.cacheVersion);
  }

  const download = await requestPresignedDownload(normalizedUri);
  return download?.presignedUrl ?? null;
};

export const requestPresignedDownload = async (uri: string): Promise<PresignDownloadItem | null> => {
  const downloads = await requestPresignedDownloads([uri]);
  return downloads[0] ?? null;
};

/** pressign-download 한 요청당 URI 개수 제한 */
export const PRESIGN_DOWNLOAD_URI_LIMIT = 5;

const requestPresignedDownloadsChunk = async (
  normalizedUris: string[]
): Promise<PresignDownloadItem[]> => {
  const url = getApiV1Url('/storage/presign-download');
  const requestBody = { uris: normalizedUris };
  const headers = getStorageApiHeaders();

  logStorage('① pressign-download 요청 시작', {
    url,
    method: 'POST',
    uriCount: normalizedUris.length,
    body: requestBody,
  });

  const startedAt = performance.now();

  let response: Response;
  try {
    response = await fetch(url, {
      method: 'POST',
      headers,
      body: JSON.stringify(requestBody),
    });
  } catch (error) {
    logStorageError('① pressign-download 네트워크 오류', error);
    throw error;
  }

  logStorage('① pressign-download HTTP 응답', {
    status: response.status,
    statusText: response.statusText,
    ok: response.ok,
    elapsedMs: Math.round(performance.now() - startedAt),
  });

  if (!response.ok) {
    const statusCode = response.status;
    const errorData = await response.json().catch(() => ({}));
    logStorageError('① pressign-download 실패', {
      statusCode,
      errorData,
      uris: normalizedUris,
    });
    throw new Error(CommonUtils.getHttpErrorMessage(statusCode, errorData));
  }

  const result: PresignDownloadResponse = await response.json();
  logStorage('① pressign-download 응답 본문', sanitizePresignDownloadResponseForLog(result));

  const downloads = result.data?.downloads ?? [];
  if (downloads.length === 0) {
    logStorageError('① pressign-download downloads 비어 있음', {
      uris: normalizedUris,
      result,
    });
  }

  return downloads;
};

/**
 * 여러 스토리지 URI에 대한 pressign-download.
 * API 한도(5개)를 초과하면 청크로 나눠 순차 호출합니다.
 */
export const requestPresignedDownloads = async (
  uris: string[]
): Promise<PresignDownloadItem[]> => {
  if (uris.length === 0) return [];

  const normalizedUris = uris.map((uri) => (uri.startsWith('/') ? uri : `/${uri}`));
  const results: PresignDownloadItem[] = [];

  for (let i = 0; i < normalizedUris.length; i += PRESIGN_DOWNLOAD_URI_LIMIT) {
    const chunk = normalizedUris.slice(i, i + PRESIGN_DOWNLOAD_URI_LIMIT);
    logStorage('① pressign-download 청크', {
      chunkIndex: Math.floor(i / PRESIGN_DOWNLOAD_URI_LIMIT) + 1,
      chunkSize: chunk.length,
      total: normalizedUris.length,
    });
    const downloads = await requestPresignedDownloadsChunk(chunk);
    results.push(...downloads);
  }

  return results;
};

export const requestPresignedUploads = async (
  params: PresignUploadParams
): Promise<PresignUploadItem[]> => {
  const url = getApiV1Url('/storage/presign-upload');
  const requestBody = {
    domain: params.domain,
    ...(params.projectCode ? { projectCode: params.projectCode } : {}),
    files: params.files,
  };
  const headers = getStorageApiHeaders();

  logStorage('① presign-upload 요청 시작', {
    url,
    method: 'POST',
    headers,
    body: requestBody,
  });

  const startedAt = performance.now();

  let response: Response;
  try {
    response = await fetch(url, {
      method: 'POST',
      headers,
      body: JSON.stringify(requestBody),
    });
  } catch (error) {
    logStorageError('① presign-upload 네트워크 오류', error);
    throw error;
  }

  logStorage('① presign-upload HTTP 응답', {
    status: response.status,
    statusText: response.statusText,
    ok: response.ok,
    elapsedMs: Math.round(performance.now() - startedAt),
  });

  if (!response.ok) {
    const statusCode = response.status;
    const errorData = await response.json().catch(() => ({}));
    logStorageError('① presign-upload 실패', { statusCode, errorData });
    throw new Error(CommonUtils.getHttpErrorMessage(statusCode, errorData));
  }

  const result: PresignUploadResponse = await response.json();
  logStorage('① presign-upload 응답 본문', sanitizePresignResponseForLog(result));

  const uploads = result.data?.uploads;
  if (!uploads?.length) {
    logStorageError('① presign-upload uploads 비어 있음', result);
    throw new Error('presigned URL 발급에 실패했습니다.');
  }

  logStorage('① presign-upload 완료', {
    uploadCount: uploads.length,
    uploads: uploads.map((item) => ({
      objectKey: item.objectKey,
      originalFileName: item.originalFileName,
      storedFileName: item.storedFileName,
      contentType: item.contentType,
      fileSize: item.fileSize,
      expiresAt: item.expiresAt,
      presignedUrl: truncatePresignedUrl(item.presignedUrl),
    })),
  });

  return uploads;
};

export const uploadFileToPresignedUrl = async (
  file: File,
  upload: PresignUploadItem
): Promise<S3UploadResult> => {
  logStorage('② S3 PUT 업로드 시작', {
    fileName: file.name,
    fileSize: file.size,
    contentType: upload.contentType,
    objectKey: upload.objectKey,
    presignedUrl: truncatePresignedUrl(upload.presignedUrl),
  });

  const startedAt = performance.now();

  let response: Response;
  try {
    response = await fetch(upload.presignedUrl, {
      method: 'PUT',
      body: file,
      headers: {
        'Content-Type': upload.contentType,
      },
    });
  } catch (error) {
    logStorageError('② S3 PUT 네트워크/CORS 오류', error);
    throw error;
  }

  const headers: Record<string, string> = {};
  response.headers.forEach((value, key) => {
    headers[key] = value;
  });

  const body = await response.text();

  const result: S3UploadResult = {
    ok: response.ok,
    status: response.status,
    statusText: response.statusText,
    headers,
    body: body || null,
  };

  logStorage('② S3 PUT 업로드 응답', {
    ...result,
    elapsedMs: Math.round(performance.now() - startedAt),
  });

  if (!response.ok) {
    logStorageError('② S3 PUT 업로드 실패', result);
    throw new Error(`S3 업로드에 실패했습니다. (${response.status} ${response.statusText})`);
  }

  logStorage('② S3 PUT 업로드 완료', {
    objectKey: upload.objectKey,
    etag: headers.etag ?? headers.ETag ?? null,
  });

  return result;
};

/** 단일 파일 presign 발급 후 S3 업로드 */
export const uploadSingleFileViaPresign = async (
  file: File,
  params: Omit<PresignUploadParams, 'files'>
): Promise<{ upload: PresignUploadItem; s3Result: S3UploadResult }> => {
  const contentType = resolveImageContentType(file);
  if (!contentType) {
    logStorageError('업로드 시작 실패 — 허용되지 않은 파일 형식', {
      fileName: file.name,
      fileType: file.type,
    });
    throw new Error('PNG, JPG, JPEG, WEBP 파일만 업로드 가능합니다');
  }

  console.group(`${LOG_PREFIX} 파일 업로드 플로우 시작`);
  logStorage('시작', {
    domain: params.domain,
    projectCode: params.projectCode ?? null,
    file: {
      name: file.name,
      size: file.size,
      type: file.type,
      resolvedContentType: contentType,
    },
  });

  const flowStartedAt = performance.now();

  try {
    const uploads = await requestPresignedUploads({
      ...params,
      files: [
        {
          fileName: file.name,
          contentType,
          fileSize: file.size,
        },
      ],
    });

    const upload = uploads[0];
    const s3Result = await uploadFileToPresignedUrl(file, upload);

    logStorage('③ 업로드 플로우 완료', {
      elapsedMs: Math.round(performance.now() - flowStartedAt),
      objectKey: upload.objectKey,
      s3Status: s3Result.status,
    });

    return { upload, s3Result };
  } catch (error) {
    logStorageError('③ 업로드 플로우 실패', {
      elapsedMs: Math.round(performance.now() - flowStartedAt),
      error,
    });
    throw error;
  } finally {
    console.groupEnd();
  }
};
